import { Button, message, Modal, ModalProps, Radio, Select } from 'antd';
import { useAuthorization } from 'components/Authorization';
import StatusButton from 'components/StatusButton';
import { useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  ConversationTodoInfo,
  getImportantTodoType,
  ImportantTodoScope,
  updateImportantTodoType,
} from 'services/api/conversation.convList';
import { useGetTodoTypeLabel } from 'views/Todo/hooks/useLabel';

export const importantTodoState = atom<
  | {
      importantTodoTypes: number[];
      options: ConversationTodoInfo[];
      scope?: ImportantTodoScope;
    }
  | undefined
>(undefined);
export const useImportantTodoValue = () => useAtomValue(importantTodoState);
export const useSetImportantTodo = () => useSetAtom(importantTodoState);

export const useGetImportantScopeValue = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const hasSupervisorPermission = payload?.hasSupervisorPermission;
  return (scope?: ImportantTodoScope) => {
    if (!scope || scope === ImportantTodoScope.InvalidQueryScope) {
      return hasSupervisorPermission ? ImportantTodoScope.All : ImportantTodoScope.OnlyMy;
    }
    return scope;
  };
};

export const useOnlyMyValue = () => {
  const todoValue = useImportantTodoValue();
  const getImportantScopeValue = useGetImportantScopeValue();

  return useMemo(() => {
    if (todoValue?.scope) {
      const scope = getImportantScopeValue(todoValue.scope);
      return scope === ImportantTodoScope.OnlyMy;
    }
  }, [getImportantScopeValue, todoValue?.scope]);
};

export function ImportantTodoModal({
  onUpdate,
  ...otherProps
}: ModalProps & { onUpdate: () => void }) {
  const { t, getTodoTypeLabel } = useGetTodoTypeLabel();

  const importantTodo = useImportantTodoValue();
  const setImportantTodo = useSetImportantTodo();

  const getImportantScopeValue = useGetImportantScopeValue();

  const [todoTypeValue, setToDoTypeValue] = useState<number[]>(
    importantTodo?.importantTodoTypes || [],
  );
  const [scope, setScope] = useState<ImportantTodoScope | undefined>(importantTodo?.scope);

  const radioValue = useMemo(() => getImportantScopeValue(scope), [scope, getImportantScopeValue]);

  const handleSave = () => {
    if (
      isEqual(todoTypeValue, importantTodo?.importantTodoTypes) &&
      isEqual(scope, importantTodo?.scope)
    ) {
      return Promise.resolve().then(() => otherProps.onCancel?.({} as any));
    } else {
      return updateImportantTodoType({ type: todoTypeValue, scope })
        .then(() => {
          setImportantTodo({
            importantTodoTypes: todoTypeValue,
            options: importantTodo?.options || [],
            scope,
          });
          message.success(t('phrase.保存成功'));
          otherProps.onCancel?.({} as any);
          onUpdate();
        })
        .catch(() => {
          message.error(t('phrase.保存失败，请稍后再试'));
        });
    }
  };

  useEffect(() => {
    if (otherProps.visible && !importantTodo) {
      getImportantTodoType()
        .then(({ todoTypeName, options }) => {
          const importantTodoTypes = todoTypeName.map(({ type }) => type);
          setImportantTodo({
            importantTodoTypes,
            options,
          });
        })
        .catch(() => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
        });
    }
  }, [importantTodo, otherProps.visible, setImportantTodo, t]);

  useEffect(() => {
    setToDoTypeValue(importantTodo?.importantTodoTypes || []);
    setScope(importantTodo?.scope || ImportantTodoScope.InvalidQueryScope);
  }, [importantTodo?.importantTodoTypes, importantTodo?.scope]);

  const options = importantTodo?.options?.map(({ type, typeName }) => {
    return {
      value: type,
      label: getTodoTypeLabel(type, typeName),
    };
  });

  return (
    <Modal
      width={512}
      title={t('重要待办')}
      footer={
        <div>
          <Button onClick={otherProps.onCancel}>{t('取消')}</Button>
          <StatusButton type="primary" onClick={handleSave}>
            {t('保存')}
          </StatusButton>
        </div>
      }
      {...otherProps}
    >
      <p>{t('选择待办类型')}</p>
      <Select
        mode="multiple"
        dropdownMatchSelectWidth={false}
        style={{ width: '100%' }}
        placeholder={t('选择待办类型')}
        value={todoTypeValue}
        onChange={setToDoTypeValue}
        options={options}
      />
      <p className="mt-4">{t('选择查看的待办')}</p>
      <Radio.Group
        value={radioValue}
        onChange={(e) => {
          const value = e.target.value;
          setScope(value as ImportantTodoScope);
        }}
      >
        <Radio value={ImportantTodoScope.All}>{t('查看全部待办项')}</Radio>
        <Radio value={ImportantTodoScope.OnlyMy}>{t('仅查看我受理的待办项')}</Radio>
      </Radio.Group>
    </Modal>
  );
}
